import { useState } from 'react';
import {
  Button,
  Chip,
  DownloadIcon,
  HistoryIcon,
  Stack,
  Text,
  Tooltip,
} from '@billda/design-system';
import {
  setCurrentVersion,
  getVersionDownloadUrl,
  type FileVersionDto,
} from '../../api/filesApi';
import { formatFileSize } from './formatFileSize';
import ConfirmDialog from '../../../../shared/components/ui/ConfirmDialog';
import { getErrorMessage } from '@billda/shared-ui';
import { useSnackbarStore } from '../../../../shared/stores/snackbarStore';

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

interface VersionHistorySectionProps {
  versions: FileVersionDto[];
  fileId: string;
  onVersionChange: () => void;
}

export function VersionHistorySection({
  versions,
  fileId,
  onVersionChange,
}: VersionHistorySectionProps) {
  const showSnackbar = useSnackbarStore(s => s.showSnackbar);
  const [confirmVersion, setConfirmVersion] = useState<FileVersionDto | null>(
    null
  );
  const [isSettingCurrent, setIsSettingCurrent] = useState(false);

  const handleSetCurrent = async (version: FileVersionDto) => {
    setIsSettingCurrent(true);
    try {
      await setCurrentVersion(fileId, version.id);
      showSnackbar(
        `Version ${version.versionNumber} is now the current version`,
        'success'
      );
      onVersionChange();
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
    } finally {
      setIsSettingCurrent(false);
      setConfirmVersion(null);
    }
  };

  const handleDownload = async (version: FileVersionDto) => {
    try {
      const { url } = await getVersionDownloadUrl(fileId, version.id);
      window.open(url, '_blank');
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
    }
  };

  if (versions.length === 0) {
    return null;
  }

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-border-default bg-surface-default">
        {versions.map((version, index) => (
          <div key={version.id}>
            <div
              data-testid={`version-row-${version.id}`}
              className="flex items-center gap-4 px-4 py-3 hover:bg-surface-raised"
            >
              <div className="min-w-0 flex-1">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Text variant="body2" className="font-semibold">
                    v{version.versionNumber}
                  </Text>
                  {version.isCurrent && (
                    <Chip label="Current" size="sm" color="primary" />
                  )}
                </Stack>
                <Text variant="caption" color="secondary">
                  {formatFileSize(version.fileSizeBytes)} &middot;{' '}
                  {version.uploadedByName} &middot;{' '}
                  {dateFormatter.format(new Date(version.uploadedAt))}
                </Text>
                {version.notes && (
                  <Text
                    variant="caption"
                    color="secondary"
                    className="mt-0.5 block italic"
                  >
                    {version.notes}
                  </Text>
                )}
              </div>

              <Stack direction="row" spacing={0.5} className="shrink-0">
                {!version.isCurrent && (
                  <Tooltip content="Set as current">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      aria-label="Set as current"
                      onClick={() => setConfirmVersion(version)}
                      disabled={isSettingCurrent}
                      data-testid={`set-current-button-${version.id}`}
                    >
                      <HistoryIcon className="size-4" aria-hidden="true" />
                    </Button>
                  </Tooltip>
                )}
                <Tooltip content="Download this version">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    aria-label="Download this version"
                    onClick={() => handleDownload(version)}
                    data-testid={`version-download-button-${version.id}`}
                  >
                    <DownloadIcon className="size-4" aria-hidden="true" />
                  </Button>
                </Tooltip>
              </Stack>
            </div>
            {index < versions.length - 1 && (
              <div className="mx-4 border-b border-border-default" />
            )}
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={confirmVersion !== null}
        title="Restore version?"
        message={
          confirmVersion
            ? `Set v${confirmVersion.versionNumber} as the current version? Downloads will receive this version.`
            : ''
        }
        confirmText="Set as current"
        onConfirm={() => {
          if (confirmVersion) {
            handleSetCurrent(confirmVersion);
          }
        }}
        onCancel={() => setConfirmVersion(null)}
      />
    </>
  );
}
